import { API } from 'aws-amplify';
import { t } from 'i18next';
import { useState, useContext, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';

import { enrollmentChangeTypes } from '../../utils/enums';
import { ProtectedRouteContext } from '../Context';
import Button from '../shared/Button';
import Grid from '../shared/Grid';
import DateTimeText from '../shared/DateTimeText';
import View from '../shared/View';
import EnrollmentModal from './modals/EnrollmentModal';

import './EnrollmentChange.scss';

function EnrollmentChange(props) {
    const { displayModal } = useContext(ProtectedRouteContext);
    const history = useHistory();

    const [applicationId] = useState(parseInt(props.match.params.applicationId));
    const [doingAjax, setDoingAjax] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [loading, setLoading] = useState(true);
    const [records, setRecords] = useState();
    const [data, setData] = useState();
    const [name, setName] = useState('');
    const [uid, setUID] = useState('');

    const headers = useMemo(() => {
        return [
            {
                colName: 'EnrollmentChangeReasonID',
                colLabel: t('EnrollmentChange.ReasonHeader'),
            },
            {
                colName: 'ExitingSchool',
                colLabel: t('EnrollmentChange.ExitSchoolHeader'),
            },
            {
                colName: 'ExitDate',
                colLabel: t('EnrollmentChange.ExitDateHeader'),
            },
            {
                colName: 'EnteringSchool',
                colLabel: t('EnrollmentChange.EnterSchoolHeader'),
            },
            {
                colName: 'EntryDate',
                colLabel: t('EnrollmentChange.EntryDateHeader'),
            },
            {
                colName: 'LatestChangeRecord',
                colLabel: t('EnrollmentChange.ActionsHeader'),
            },
        ];
    }, []);

    const handleBack = useMemo(() => {
        return () => history.goBack();
    }, [history]);

    const displayEnrollmentChange = useMemo(() => {
        return (toggle, _enrollID, _type) => {
            setDoingAjax(toggle);
            let modal = toggle ? (
                <EnrollmentModal
                    enrollID={_enrollID}
                    type={_type}
                    appInstId={applicationId}
                    cancelFunction={() => {
                        setDoingAjax(false);
                        displayModal(false);
                    }}
                    onStatusChange={() => {
                        setDoingAjax(false);
                        displayModal(false);
                        setInitialized(false);
                    }}
                />
            ) : null;
            displayModal(toggle, modal);
        };
    }, [displayModal, applicationId]);

    const gridJSX = useMemo(() => {
        if (initialized && headers && data?.rows?.length > 0) {
            return (
                <Grid
                    title="Enrollment History"
                    name="enrollment-change-grid"
                    className="enrollment-change-grid"
                    headers={headers}
                    data={data}
                    filters={false}
                    pageSize={100}
                    search={false}
                    hoverable={true}
                    counter={false}
                />
            );
        } else if (loading) return null;
        else return <p>{t('General.NoResults')}</p>;
    }, [initialized, loading, headers, data]);

    const enrollmentTitle = useMemo(() => {
        return (
            <div className="enrollment-change-view-title">
                {!loading
                    ? t('EnrollmentChange.Title', {
                          ApplicantName: name,
                          UniqueID: uid,
                      })
                    : `${t('General.Loading')}...`}
            </div>
        );
    }, [name, uid, loading]);

    useEffect(() => {
        if (initialized && typeof records?.map === 'function') {
            const addColumns = (a) => {
                let _keys = {};

                for (const [key, value] of Object.entries(a)) {
                    switch (key) {
                        case 'EntryDate':
                        case 'ExitDate':
                            _keys[key] = !!value ? value : new Date('1/1/3000').toISOString();
                            break;
                        default:
                            _keys[key] = typeof value === 'boolean' ? value : !!value ? value : '';
                    }
                }

                let changeReason;

                switch (a.EnrollmentChangeReasonID) {
                    case enrollmentChangeTypes.Initial:
                        changeReason = t('EnrollmentChange.InitialReason');
                        break;
                    case enrollmentChangeTypes.Renewal:
                        changeReason = t('EnrollmentChange.RenewalReason');
                        break;
                    case enrollmentChangeTypes.Transfer:
                        changeReason = t('EnrollmentChange.TransferReason');
                        break;
                    case enrollmentChangeTypes.Withdrawal:
                        changeReason = t('EnrollmentChange.WithdrawalReason');
                        break;
                    case enrollmentChangeTypes.ImportInitial:
                        changeReason = t('EnrollmentChange.ImportInitialReason');
                        break;
                    case enrollmentChangeTypes.ImportRenewal:
                        changeReason = t('EnrollmentChange.ImportRenewalReason');
                        break;
                    default:
                        changeReason = t('EnrollmentChange.UnknownReason');
                }

                let readOnlyTypes = [
                    enrollmentChangeTypes.Initial,
                    enrollmentChangeTypes.Renewal,
                    enrollmentChangeTypes.ImportInitial,
                    enrollmentChangeTypes.ImportRenewal,
                ].includes(a.EnrollmentChangeReasonID);

                let buttonJSX =
                    a.LatestChangeRecord && !readOnlyTypes ? (
                        <div>
                            <button
                                className="button-link"
                                onClick={() => displayEnrollmentChange(true, a.EnrollmentID, 2)}
                            >
                                {t('General.Edit')}
                            </button>{' '}
                            |{' '}
                            <button
                                className="button-link red"
                                onClick={() => displayEnrollmentChange(true, a.EnrollmentID, 4)}
                            >
                                {t('General.Delete')}
                            </button>
                        </div>
                    ) : (
                        <button
                            className="button-link"
                            onClick={() => displayEnrollmentChange(true, a.EnrollmentID, 3)}
                        >
                            {t('General.View')}
                        </button>
                    );

                let columns = [
                    <span colname="EnrollmentChangeReasonID">
                        <ReactMarkdown
                            disallowedElements={['p']}
                            unwrapDisallowed={true}
                        >
                            {changeReason}
                        </ReactMarkdown>
                    </span>,
                    <span colname="ExitingSchool">{!!a.ExitingSchool ? a.ExitingSchool : ''}</span>,
                    <span colname="ExitDate">
                        {!!a.ExitDate ? (
                            <DateTimeText
                                date={a.ExitDate}
                                type="short"
                            />
                        ) : (
                            ''
                        )}
                    </span>,
                    <span colname="EntrySchool">{!!a.EnteringSchool ? a.EnteringSchool : ''}</span>,
                    <span colname="EntryDate">
                        {!!a.EntryDate ? (
                            <DateTimeText
                                date={a.EntryDate}
                                type="short"
                            />
                        ) : (
                            ''
                        )}
                    </span>,
                    <span
                        colname="LatestChangeRecord"
                        className="actions-column"
                    >
                        {buttonJSX}

                        <span>
                            {a.LastUpdatedUTC ? t('EnrollmentChange.UpdatedInfo') : t('EnrollmentChange.CreatedInfo')}&nbsp;
                            {a.LastUpdatedUTC ? <DateTimeText date={a.LastUpdatedUTC} /> : <DateTimeText date={a.CreatedUTC} />}
                        </span>
                    </span>,
                ];

                return Object.assign({}, _keys, { columns: columns });
            };

            setData({
                rows: records.map(addColumns),
            });
        }
    }, [initialized, records, displayEnrollmentChange]);

    useEffect(() => {
        async function getHistory() {
            setInitialized(true);
            setDoingAjax(true);

            try {
                let _response = await API.get('ShipyardAPI', `/application/${applicationId}/enrollment_history`);

                setRecords(_response.enrollmentHistory);

                setName(
                    `${_response?.applicantInfo?.find((o) => o.questionId === 33)?.answerText} ${_response?.applicantInfo?.find((o) => o.questionId === 34)?.answerText}`
                );

                setUID(_response?.applicantInfo?.find((o) => o.questionId === 93)?.answerText);

                setLoading(false);
            } catch (e) {
                toast.error(t('General.AjaxError'));
            } finally {
                setDoingAjax(false);
            }
        }

        if (!initialized) getHistory();
    }, [initialized, applicationId]);

    return (
        <View
            doingAjax={doingAjax}
            viewTitle={enrollmentTitle}
            className="enrollment-change-view"
            name="enrollment-change"
        >
            <button
                className="back-button button-link"
                onClick={handleBack}
            >
                <FontAwesomeIcon icon={faArrowLeft} /> {t('EnrollmentChange.BackButtonLabel')}
            </button>
            {gridJSX}

            <Button
                className="change-enrollment-button"
                onClick={() => {
                    displayEnrollmentChange(true, undefined, 1);
                }}
                interactive={false}
            >
                {t('EnrollmentChange.ChangeButtonLabel')}
            </Button>
        </View>
    );
}

export default EnrollmentChange;
